import { getGetFrontAreaPubblicaRichiesteIdModuliQueryOptions } from '@framework/api-client/api/inquiries-compile/area-pubblica-osservazioni-controller/area-pubblica-osservazioni-controller.ts';
import { getGetFrontRichiesteIdModuliQueryOptions } from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { useGetQuery } from '@framework/api-client/utils/hooks/useGetQuery.ts';
import produce from 'immer';
import { sortBy } from 'lodash';
import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { isPublicSubmission } from '../isPublicSubmission.ts';

export interface IUseSubmissionModulesParams {
  submissionId: string | null | undefined;
  procedureId?: string | null | undefined;
}

/**
 * Ritorna l'API corretta da chiamare in base al path fornito.
 * Nel caso di un path che contiene 'osservazioni' ritorna l'endpoint per le richieste pubbliche.
 * Altrimenti, ritorna l'endpoint per le richieste dell'utente.
 */
function getSubmissionModuleEndpoint(path: string) {
  if (isPublicSubmission(path)) {
    // GET/area-pubblica/richieste/{id}/moduli
    return getGetFrontAreaPubblicaRichiesteIdModuliQueryOptions;
  }

  // GET/richesta/{id}/moduli
  return getGetFrontRichiesteIdModuliQueryOptions;
}

/**
 * Ritorna la richiesta in base all'id fornito, richiamando l'API
 * "privata" se si sta guardando una propria richiesta, o quella "pubblica"
 * se si cerca una richiesta pubblica.
 */
function useSubmissionModulesById(props: IUseSubmissionModulesParams) {
  const { submissionId, procedureId } = props;
  const location = useLocation();

  const queryOptions = useMemo(
    () => getSubmissionModuleEndpoint(location.pathname),
    [location.pathname],
  );

  // I due endpoint ora sono differenti. Quello privato ha come input extra dei params
  const params = useMemo(() => {
    if (isPublicSubmission(location.pathname)) {
      //[query config , <undefined>]
      return [{ query: { enabled: Boolean(submissionId) } }, undefined];
    }

    // [params , query config]
    return [
      { idIstanzaProcedimento: procedureId },
      { query: { enabled: Boolean(submissionId) } },
    ];
  }, [location.pathname, procedureId, submissionId]);

  const result = useGetQuery(queryOptions, [
    submissionId ?? '<unknown>',
    params[0] as any,
    params[1] as any,
  ]);

  return result;
}

export type ISubmissionModules = ReturnType<typeof useSubmissionModules>;

export function useSubmissionModules(params: IUseSubmissionModulesParams) {
  const { data, isLoading, isError, error } = useSubmissionModulesById(params);

  const modules = useMemo(() => {
    if (!data) return [];
    return produce(data, draft => {
      // Riordino i moduli
      draft?.forEach(module => {
        if (!module.modulo?.sezioni) return;
        module.modulo.sezioni = sortBy(module.modulo.sezioni, 'ordine');
      });

      return;
    });
  }, [data]);

  return { modules, isLoading, isError, error };
}
